import './lib/zodGlobalConfig';
import './services/sentryBootstrap';
import { StrictMode, lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { createQueryClient } from './lib/query/queryClient'
import { i18nReady } from './lib/i18n';
import './index.css';
import App from './App.tsx'
import ErrorBoundary from './components/ErrorBoundary';
import { installChunkReloadHandler } from './lib/chunkReload';
import { restoreSession } from './lib/api';

// Recover from stale lazy-route chunks after a new deploy (Sentry BEETEGRE-V2-1).
installChunkReloadHandler();

const queryClient = createQueryClient();

// Devtools are loaded only in development and only when NOT running inside the
// component-playground iframe (?frame=1). The iframe mounts the full app, so
// without this guard a second floating devtools button appears in the iframe.
const isPlaygroundFrame =
  import.meta.env.DEV &&
  new URLSearchParams(window.location.search).get('frame') === '1';

const ReactQueryDevtools = import.meta.env.DEV && !isPlaygroundFrame
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then((m) => ({
        default: m.ReactQueryDevtools,
      })),
    )
  : null;

// Persisted session without an in-memory token (every reload): mint it now instead of letting the
// first protected requests 401 and recover reactively. Not awaited, so the shell is not held behind
// the network; the flag is raised synchronously here, before the first render, and the route guards
// show a skeleton until it clears. restoreSession clears the flag in `finally`; it can still reject,
// so the catch here keeps that from surfacing as an unhandled rejection.
restoreSession().catch((err: unknown) => console.error('[API] Oturum geri yükleme başarısız', err));

const renderApp = () =>
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
        {ReactQueryDevtools && (
          <Suspense fallback={null}>
            <ReactQueryDevtools initialIsOpen={false} />
          </Suspense>
        )}
      </QueryClientProvider>
    </StrictMode>,
  );

// A stored non-default language is fetched on demand; rendering before it lands would paint
// the fallback language for a moment and then swap. A failed fetch still renders (fallback).
i18nReady.then(renderApp, renderApp);
